/* JPI universal Langify and Shopify Inbox coordination. */
(() => {
  const LANGUAGE_SELECTOR = '.ly-switcher-wrapper';
  const CHAT_SELECTOR = 'shopify-chat, #ShopifyChat, shopify-agent';
  const NATIVE_SELECTOR = [
    '.jpi-universal-utilities[data-jpi-localization-mode="native"]',
    '.jpi-header__localization',
    '.jpi-header__mobile-localization'
  ].join(',');
  const LANGUAGE_PRESENTATION = {
    en: { label: 'English', lang: 'en', direction: 'ltr', script: 'latin' },
    es: { label: 'Español', lang: 'es', direction: 'ltr', script: 'latin' },
    ar: { label: 'العربية', lang: 'ar', direction: 'rtl', script: 'arabic' },
    'zh-cn': { label: '简体中文', lang: 'zh-CN', direction: 'ltr', script: 'han' },
    hi: { label: 'हिन्दी', lang: 'hi', direction: 'ltr', script: 'devanagari' },
    ne: { label: 'नेपाली', lang: 'ne', direction: 'ltr', script: 'devanagari' },
    fr: { label: 'Français', lang: 'fr', direction: 'ltr', script: 'latin' },
    gd: { label: 'Gàidhlig', lang: 'gd', direction: 'ltr', script: 'latin' },
    ga: { label: 'Gaeilge', lang: 'ga', direction: 'ltr', script: 'latin' },
    fa: { label: 'فارسی', lang: 'fa', direction: 'rtl', script: 'arabic' },
    'pt-br': { label: 'Português (Brasil)', lang: 'pt-BR', direction: 'ltr', script: 'latin' }
  };
  const chatOpenObservers = new WeakMap();
  let scanQueued = false;

  function suppressFirstOrderOffer() {
    const shouldSuppress = Boolean(document.querySelector('[data-jpi-suppress-first-order-offer]'));
    document.documentElement.toggleAttribute('data-jpi-suppress-first-order-offer', shouldSuppress);
    if (!shouldSuppress) return;

    document.querySelectorAll('#ecomsend-widget, .EcomSendAPP').forEach((element) => {
      element.hidden = true;
      element.setAttribute('aria-hidden', 'true');
      element.style.setProperty('display', 'none', 'important');
    });

    document.querySelectorAll('.react-responsive-modal-root').forEach((root) => {
      const isFirstOrderOffer = root.querySelector(
        '.react-responsive-modal-modal.pp-modal, #trustoo-popup-optIn-title, .ecomsend__Modal__Content'
      );
      if (!isFirstOrderOffer) return;
      root.hidden = true;
      root.setAttribute('aria-hidden', 'true');
      root.style.setProperty('display', 'none', 'important');
    });
  }

  function syncUtilityOffsets(chatControls) {
    const activatorRects = Array.from(chatControls)
      .map((control) => control.shadowRoot?.querySelector('[part="activator"], .activator'))
      .filter(Boolean)
      .map((activator) => activator.getBoundingClientRect())
      .filter((rect) => rect.width > 0 && rect.height > 0);

    if (!activatorRects.length) return;

    const highestActivatorTop = Math.min(...activatorRects.map((rect) => rect.top));
    const rootStyles = getComputedStyle(document.documentElement);
    const configuredGap = Number.parseFloat(rootStyles.getPropertyValue('--jpi-app-utility-gap')) || 16;
    const requiredBottom = Math.max(0, window.innerHeight - highestActivatorTop + configuredGap);
    document.documentElement.style.setProperty('--jpi-app-utility-language-bottom', `${Math.ceil(requiredBottom)}px`);
  }

  function syncUtilityStacking() {
    const languageControls = document.querySelectorAll(LANGUAGE_SELECTOR);
    const chatControls = document.querySelectorAll(CHAT_SELECTOR);
    const chatIsOpen = Array.from(chatControls).some((control) => control.hasAttribute('open'));

    languageControls.forEach((control) => {
      control.style.setProperty('z-index', 'var(--jpi-app-utility-z-language)', 'important');
    });

    chatControls.forEach((control) => {
      control.style.setProperty('z-index', 'var(--jpi-app-utility-z-chat)', 'important');
      control.style.setProperty('--shopify-chat-z-index', 'var(--jpi-app-utility-z-chat)');
      control.style.setProperty('--shopify-agent-z-index', 'var(--jpi-app-utility-z-chat)');
      control.style.setProperty('--shopify-chat-border-radius', '24px', 'important');
      control.style.setProperty('--shopify-agent-border-radius', '24px', 'important');

      if (chatOpenObservers.has(control)) return;
      const observer = new MutationObserver(syncUtilityStacking);
      observer.observe(control, { attributes: true, attributeFilter: ['open'] });
      chatOpenObservers.set(control, observer);
    });

    document.documentElement.toggleAttribute('data-jpi-chat-open', chatIsOpen);
    syncUtilityOffsets(chatControls);
  }

  function toLanguageAbbreviation(value) {
    return String(value || '')
      .trim()
      .split('-')[0]
      .slice(0, 2)
      .toUpperCase();
  }

  function decorateLanguageControl(control) {
    const selectedLink = control.querySelector('.ly-languages-switcher-link[aria-current="true"]');
    const selectedCode = selectedLink?.dataset.languageCode || document.documentElement.lang || 'en';
    const selectedAbbreviation = toLanguageAbbreviation(selectedCode);
    const selectedPresentation = LANGUAGE_PRESENTATION[String(selectedCode).toLowerCase()] || LANGUAGE_PRESENTATION.en;

    control.querySelectorAll('.ly-custom-dropdown-current, .ly-popup-current').forEach((current) => {
      current.dataset.jpiLanguageAbbreviation = selectedAbbreviation;
      current.setAttribute('aria-label', `Selected language: ${selectedPresentation.label}`);
      const currentLabel = current.querySelector('.ly-custom-dropdown-current-inner-text');
      if (currentLabel) {
        currentLabel.textContent = selectedPresentation.label;
        currentLabel.lang = selectedPresentation.lang;
        currentLabel.dir = selectedPresentation.direction;
        currentLabel.dataset.jpiLanguageScript = selectedPresentation.script;
      }
    });

    control.querySelectorAll('.ly-languages-switcher-link[data-language-code]').forEach((link) => {
      const languageCode = link.dataset.languageCode;
      const presentation = LANGUAGE_PRESENTATION[String(languageCode).toLowerCase()];
      link.dataset.jpiLanguageAbbreviation = toLanguageAbbreviation(languageCode);
      if (!presentation) return;

      link.lang = presentation.lang;
      link.dir = 'ltr';
      const label = link.querySelector('.ly-custom-dropdown-list-element-right, .ly-inner-text');
      if (label) {
        label.textContent = presentation.label;
        label.lang = presentation.lang;
        label.dir = presentation.direction;
        label.dataset.jpiLanguageScript = presentation.script;
      }
    });
  }

  function reconcileNativeControls(hasLangify) {
    document.querySelectorAll(NATIVE_SELECTOR).forEach((control) => {
      if (hasLangify) {
        if (!control.hidden) control.dataset.jpiSuppressedByLangify = 'true';
        control.hidden = true;
      } else if (control.dataset.jpiSuppressedByLangify === 'true') {
        control.hidden = false;
        delete control.dataset.jpiSuppressedByLangify;
      }
    });
  }

  function scan() {
    scanQueued = false;
    suppressFirstOrderOffer();
    const languageControls = document.querySelectorAll(LANGUAGE_SELECTOR);
    const chatControls = document.querySelectorAll(CHAT_SELECTOR);

    languageControls.forEach((control) => {
      control.dataset.jpiUniversalAppUtility = 'language';
      control.classList.add('jpi-app-utility--language');
      decorateLanguageControl(control);
    });

    chatControls.forEach((control) => {
      control.dataset.jpiUniversalAppUtility = 'chat';
      control.classList.add('jpi-app-utility--chat');
    });

    document.documentElement.toggleAttribute('data-jpi-has-langify', languageControls.length > 0);
    document.documentElement.toggleAttribute('data-jpi-has-shopify-chat', chatControls.length > 0);
    reconcileNativeControls(languageControls.length > 0);
    syncUtilityStacking();
  }

  function scheduleScan() {
    if (scanQueued) return;
    scanQueued = true;
    window.requestAnimationFrame(scan);
  }

  function start() {
    scan();
    if (!document.documentElement) return;

    const observer = new MutationObserver(scheduleScan);
    observer.observe(document.documentElement, { childList: true, subtree: true });
    window.setTimeout(() => observer.disconnect(), 15000);

    window.addEventListener('load', scan, { once: true });
    window.addEventListener('resize', scheduleScan, { passive: true });
    document.addEventListener('shopify:section:load', scheduleScan);
    document.addEventListener('shopify:block:select', scheduleScan);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
